import { AssessmentType } from '../types';

interface DassScores {
  depression: {
    score: number;
    severity: string;
    color: string;
  };
  anxiety: {
    score: number;
    severity: string;
    color: string;
  };
  stress: {
    score: number;
    severity: string;
    color: string;
  };
}

interface BrsScores {
  score: number;
  severity: string;
  color: string;
}

interface CissScores {
  task: {
    score: number;
    percentage: number;
    interpretation: string;
    color: string;
  };
  emotion: {
    score: number;
    percentage: number;
    interpretation: string;
    color: string;
  };
  avoidance: {
    score: number;
    percentage: number;
    interpretation: string;
    color: string;
  };
  primaryStyle: 'task' | 'emotion' | 'avoidance';
}

// Question mappings for each category
const questionMappings = {
  depression: [3, 5, 10, 13, 16, 17, 21, 24, 26, 31, 34, 37, 38, 42],
  anxiety: [2, 4, 7, 9, 15, 19, 20, 23, 25, 28, 30, 36, 40, 41],
  stress: [1, 6, 8, 11, 12, 14, 18, 22, 27, 29, 32, 33, 35, 39]
};

const severityLevels = {
  depression: [
    { max: 13, label: 'Normal', color: 'bg-green-100 text-green-800' },
    { max: 19, label: 'Mild', color: 'bg-yellow-100 text-yellow-800' },
    { max: 28, label: 'Moderate', color: 'bg-orange-100 text-orange-800' },
    { max: 37, label: 'Severe', color: 'bg-red-100 text-red-800' },
    { max: Infinity, label: 'Extremely Severe', color: 'bg-red-200 text-red-900' }
  ],
  anxiety: [
    { max: 9, label: 'Normal', color: 'bg-green-100 text-green-800' },
    { max: 14, label: 'Mild', color: 'bg-yellow-100 text-yellow-800' },
    { max: 20, label: 'Moderate', color: 'bg-orange-100 text-orange-800' },
    { max: 27, label: 'Severe', color: 'bg-red-100 text-red-800' },
    { max: Infinity, label: 'Extremely Severe', color: 'bg-red-200 text-red-900' }
  ],
  stress: [
    { max: 19, label: 'Normal', color: 'bg-green-100 text-green-800' },
    { max: 25, label: 'Mild', color: 'bg-yellow-100 text-yellow-800' },
    { max: 35, label: 'Moderate', color: 'bg-orange-100 text-orange-800' },
    { max: 45, label: 'Severe', color: 'bg-red-100 text-red-800' },
    { max: Infinity, label: 'Extremely Severe', color: 'bg-red-200 text-red-900' }
  ],
  brs: [
    { max: 2.99, label: 'Low Resilience', color: 'bg-red-100 text-red-800' },
    { max: 4.30, label: 'Normal Resilience', color: 'bg-green-100 text-green-800' },
    { max: Infinity, label: 'High Resilience', color: 'bg-blue-100 text-blue-800' }
  ]
};

export const calculateDassScores = (answers: Record<number, number>): DassScores => {
  const calculateCategoryScore = (category: keyof typeof questionMappings) => {
    const score = questionMappings[category].reduce((sum, questionId) => {
      return sum + (answers[questionId] || 0);
    }, 0);

    const severity = severityLevels[category].find(level => score <= level.max);
    return {
      score,
      severity: severity?.label || 'Extremely Severe',
      color: severity?.color || 'bg-red-200 text-red-900'
    };
  };

  return {
    depression: calculateCategoryScore('depression'),
    anxiety: calculateCategoryScore('anxiety'),
    stress: calculateCategoryScore('stress')
  };
};

export const calculateBrsScores = (answers: Record<number, number>): BrsScores => {
  // Questions 2, 4, and 6 need reverse scoring
  const reverseQuestions = [2, 4, 6];
  
  // Calculate total score
  let totalScore = 0;
  for (let i = 1; i <= 6; i++) {
    let score = answers[i] || 0;
    
    // Convert from 0-4 scale to 1-5 scale
    score = score + 1;
    
    // Apply reverse scoring for questions 2, 4, and 6
    if (reverseQuestions.includes(i)) {
      score = 6 - score; // Reverse on 1-5 scale (5->1, 4->2, 3->3, 2->4, 1->5)
    }
    
    totalScore += score;
  }

  // Calculate average score (on 1-5 scale)
  const averageScore = totalScore / 6;

  // Determine severity level
  const severity = severityLevels.brs.find(level => averageScore <= level.max);

  return {
    score: averageScore,
    severity: severity?.label || 'Low Resilience',
    color: severity?.color || 'bg-red-100 text-red-800'
  };
};

export const calculateCissScores = (answers: Record<number, number>): CissScores => {
  // Calculate scores for each coping style
  const taskScores = Array.from({ length: 16 }, (_, i) => answers[i + 1] || 0);
  const emotionScores = Array.from({ length: 16 }, (_, i) => answers[i + 17] || 0);
  const avoidanceScores = Array.from({ length: 16 }, (_, i) => answers[i + 33] || 0);

  const taskTotal = taskScores.reduce((a, b) => a + b, 0);
  const emotionTotal = emotionScores.reduce((a, b) => a + b, 0);
  const avoidanceTotal = avoidanceScores.reduce((a, b) => a + b, 0);

  // Calculate percentages (max score per style is 80)
  const taskPercentage = (taskTotal / 80) * 100;
  const emotionPercentage = (emotionTotal / 80) * 100;
  const avoidancePercentage = (avoidanceTotal / 80) * 100;

  // Determine primary coping style
  const maxScore = Math.max(taskTotal, emotionTotal, avoidanceTotal);
  const primaryStyle = taskTotal === maxScore ? 'task' :
                      emotionTotal === maxScore ? 'emotion' : 'avoidance';

  // Generate interpretations based on scores
  const getInterpretation = (score: number, style: string) => {
    const percentage = (score / 80) * 100;
    if (percentage >= 75) return `High ${style} coping style`;
    if (percentage >= 50) return `Moderate ${style} coping style`;
    return `Low ${style} coping style`;
  };

  return {
    task: {
      score: taskTotal,
      percentage: taskPercentage,
      interpretation: getInterpretation(taskTotal, 'task-oriented'),
      color: '#4f46e5'
    },
    emotion: {
      score: emotionTotal,
      percentage: emotionPercentage,
      interpretation: getInterpretation(emotionTotal, 'emotion-oriented'),
      color: '#ef4444'
    },
    avoidance: {
      score: avoidanceTotal,
      percentage: avoidancePercentage,
      interpretation: getInterpretation(avoidanceTotal, 'avoidance'),
      color: '#f59e0b'
    },
    primaryStyle
  };
};